import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { FiArrowRight } from "react-icons/fi";
import { MdContentCopy } from "react-icons/md";
import { normalizeBillStatus } from "utils/functions";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import BannerEnergia from "components/Portal/BannerEnergia";
import BannerPromotions from "components/layout/BannerPromotions";
import BannerReclameAqui from "components/Portal/BannerReclameAqui";
import Bills from "components/layout/portal/boletos";
import CTOS from "components/layout/portal/ctos";
import Chart from "components/MI/minha-conta/Chart";
import ModalBackToUnit from "components/Portal/ModalBackToUnit";
import ModalValidationContact from "components/Portal/ModalValidationContact";
import Navbar from "components/MI/Navbar";
import TermoAdesao from "components/Portal/TermoAdesao";
import UserMenu from "components/layout/userMenuNew";
import api from "services/api";
import { useTour } from "hooks/useTour";

import BannersSlider from "components/BannersSlider";
import TourBalloon from "components/NewTour/TourBallon";
import {
  BodyBill,
  BodySpending,
  ButtonBills,
  ButtonCopy,
  Container,
  ContainerWrap,
  ContentBannerPromotions,
  ContentBill,
  ContentGrid,
  ContentSpending,
  DueDateBill,
  ExtraCard,
  FooterBill,
  FooterSpeding,
  HeaderCard,
  MonthYearBill,
  StatusCard,
  Title,
  TitleCard,
  ValueBill,
} from "./styles";
import { PesquisaIndecx } from "components/Portal/PesquisaIndecx";

const status = {
  VENCIDO: "Vencido",
  PAGO: "Pago",
  A_VENCER: "A vencer",
};

const copyToClipboard = (text: string): void => {
  navigator?.clipboard?.writeText(text);
  toast?.info(`Código copiado: ${text}`);
};

declare global {
  interface Window {
    TrackWidget: any;
  }
}

const Portal = (): JSX.Element => {
  const [billsData, setBillsData] = useState([]);
  const [spendingData, setSpendingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFirstAccess, setIsFirstAccess] = useState<boolean | null>(null);
  const selectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  const clientUserId =
    localStorage.getItem("@UltragazEmpresarial:userId") || undefined;

  useEffect(() => {
    loadApis();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const headers = {
          access_token: localStorage.getItem(
            "@UltragazEmpresarial:accessToken"
          ),
        };
        const userId = localStorage.getItem("@UltragazEmpresarial:userId");
        if (!userId) {
          setIsFirstAccess(false);
          return;
        }
        const { data } = await api.get(
          `industrial/customer/v1/users/${userId}`,
          { headers }
        );
        setIsFirstAccess(Boolean(data?.isFirstLogin));
      } catch {
        setIsFirstAccess(false);
      }
    })();
  }, []);

  useEffect(() => {
    setTourType("home");
    setSteps(10);
  }, [showTour, setTourType, setSteps]);

  useEffect(() => {
    loadApis();
  }, [selectedUnit]);

  const loadApis = async (): Promise<void> => {
    setLoading(true);
    getInvoices();
    getSpendings();
    setLoading(false);
  };

  const getSpendings = async (): Promise<void> => {
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    if (!instanceId) return;

    const endDate = moment()
      .add(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    const beginDate = moment(endDate)
      .subtract(12, "month")
      .endOf("month")
      .format("YYYY-MM-DD");

    const params = {
      document,
      "unit-id": instanceId,
      "begin-date": beginDate,
      "end-date": endDate,
    };

    const response = await api.get("industrial/bill/v1/bills/consumption", {
      params,
      headers,
    });

    const { data } = response;

    setSpendingData(data?.splice(0, 2));
  };

  const getInvoices = async (): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const accessToken = localStorage.getItem(
      "@UltragazEmpresarial:accessToken"
    );
    const { instanceId } = selectedUnit;

    const headers = {
      access_token: accessToken,
    };

    const params = {
      document,
      "unit-id": instanceId,
      _expand: "installments",
      _offset: 0,
      _limit: 5,
    };

    const response = await api.get(`industrial/bill/v1/bills`, {
      params,
      headers,
    });
    const { data } = response;

    const bankSlips = data?.reduce((previousValue, currentValue) => {
      const newValue = { invoice: currentValue, installment: currentValue };
      newValue.installment.paymentTypeBoleto = true;
      previousValue.push(newValue);
      return previousValue;
    }, []);

    setBillsData(bankSlips);
  };

  const bill = useMemo(() => {
    return billsData[0];
  }, [billsData]);

  const bills = useMemo(() => {
    return billsData?.slice(1, 5);
  }, [billsData]);

  const spending = useMemo(() => {
    const newData = spendingData
      ?.map((item, index) => {
        let monthValueColor = "#000fff";

        if (index === 0) {
          monthValueColor = "#97F8FB";
        }

        return {
          month: moment(
            item?.measurementDate?.replaceAll("-", "/"),
            "YYYY-MM-DD"
          ).format(""),
          monthValue: item?.kgConsumption.replace(",", "."),
          monthValueColor,
        };
      })
      .splice(0, 2)
      .reverse();
    return newData;
  }, [spendingData]);

  return (
    <>
      <Navbar selected="inicio">
        <UserMenu intro />
        <Container>
          <Title>Início</Title>
          {loading ? (
            <span>Carregando ...</span>
          ) : (
            <ContentGrid>
              {selectedUnit?.hasCurrentHouseholder && (
                <>
                  <ContentBill
                    showTour={tourStep === 6 && tourType === "home" && showTour}
                  >
                    <ContainerWrap>
                      <HeaderCard>
                        <TitleCard>Fatura atual</TitleCard>
                        {bill && (
                          <ExtraCard>
                            <StatusCard
                              status={normalizeBillStatus(
                                bill?.invoice?.status,
                                bill?.invoice?.totalAmount
                              )}
                            />
                            <span>
                              {
                                status[
                                  normalizeBillStatus(
                                    bill?.invoice?.status,
                                    bill?.invoice?.totalAmount
                                  )
                                ]
                              }
                            </span>
                          </ExtraCard>
                        )}
                      </HeaderCard>
                      <BodyBill>
                        {bill ? (
                          <>
                            <MonthYearBill>
                              <span>
                                {moment(
                                  `${bill?.invoice?.referenceMonth || ""}/01`
                                )?.format("MMM")}
                              </span>
                              <span>
                                {moment(
                                  `${bill?.invoice?.referenceMonth || ""}/01`
                                )?.format("yyyy")}
                              </span>
                            </MonthYearBill>
                            <DueDateBill>
                              <span>Vencimento</span>
                              <span>
                                {moment(bill?.invoice?.dueDate ?? "")?.format(
                                  "DD/MM/yyyy"
                                )}
                              </span>
                            </DueDateBill>
                            <ValueBill>
                              {Intl.NumberFormat("pt-br", {
                                style: "currency",
                                currency: "BRL",
                              }).format(
                                bill?.invoice?.totalAmount?.replace(",", ".")
                              )}
                            </ValueBill>
                          </>
                        ) : (
                          <span>Você não possui fatura atual!</span>
                        )}
                      </BodyBill>
                      <FooterBill>
                        {bill ? (
                          <ButtonCopy
                            onClick={() =>
                              copyToClipboard(bill?.invoice?.typeableLine)
                            }
                          >
                            <span>Copiar código</span>
                            <MdContentCopy />
                          </ButtonCopy>
                        ) : (
                          <div />
                        )}
                        <ButtonBills to="/consulta-debitos">
                          <span>Histórico de boletos</span>
                          <FiArrowRight />
                        </ButtonBills>
                      </FooterBill>
                      {showTour && tourStep === 6 && tourType === "home" && (
                        <TourBalloon
                          title="Fatura atual"
                          content="Aqui você encontra as informações da sua fatura atual. Ao clicar em Copiar código, de forma prática e rápida, você pode copiar os dados para pagamento do boleto."
                          position={{
                            left: "0",
                            right: "0",
                            top: "142px",
                            arrowPosition: "top",
                            maxWidth: 320,
                            arrowTop: "-38px",
                          }}
                        />
                      )}
                    </ContainerWrap>
                  </ContentBill>
                  <ContentSpending
                    showTour={tourStep === 7 && tourType === "home" && showTour}
                  >
                    <ContainerWrap>
                      <HeaderCard>
                        <TitleCard>Comparação de consumo (Kg)</TitleCard>
                      </HeaderCard>
                      <BodySpending>
                        {spending?.length ? (
                          <Chart data={spending} />
                        ) : (
                          <span>
                            Não se preocupe! No primeiro mês não virá a conta.
                          </span>
                        )}
                      </BodySpending>
                      <FooterSpeding>
                        <ButtonBills to="consumo">
                          <span>Ver todos</span>
                          <FiArrowRight />
                        </ButtonBills>
                      </FooterSpeding>
                    </ContainerWrap>
                    {showTour && tourStep === 7 && tourType === "home" && (
                      <TourBalloon
                        title="Comparação de consumo"
                        content="Aqui você tem um comparativo de seu consumo do mês anterior, em relação ao mês atual."
                        position={{
                          left: "0",
                          right: "0",
                          top: "128px",
                          arrowPosition: "top",
                          maxWidth: 320,
                          arrowTop: "-28px",
                        }}
                      />
                    )}
                  </ContentSpending>
                  {selectedUnit?.hasCurrentHouseholder && <CTOS />}
                </>
              )}
              <Bills data={bills} />
              <ContentBannerPromotions
                showTour={tourStep === 10 && tourType === "home" && showTour}
              >
                <BannersSlider />
                {showTour && tourStep === 10 && tourType === "home" && (
                  <TourBalloon
                    title="Novidades"
                    content="Aqui compartilharemos com você todas as novidades para energizar junto com a Ultragaz!."
                    position={{
                      left: "0",
                      right: "0",
                      top: "280px",
                      arrowPosition: "top",
                      maxWidth: 320,
                      arrowTop: "-28px",
                    }}
                  />
                )}
              </ContentBannerPromotions>
            </ContentGrid>
          )}
        </Container>
        {isFirstAccess && (
          <TermoAdesao
            clientUserId={
              localStorage.getItem("@UltragazEmpresarial:document")
                ? `${clientUserId}_${localStorage.getItem(
                    "@UltragazEmpresarial:document"
                  )}`
                : `${clientUserId}`
            }
          />
        )}
        <BannerEnergia />
        <ModalBackToUnit />
        <ModalValidationContact />
        <BannerReclameAqui />
        <PesquisaIndecx />
      </Navbar>
      <BannerPromotions />
    </>
  );
};

export default Portal;
